import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiBlob, apiMultipart } from '../lib/api.ts';
import { Aviso, Boton, BotonArchivo } from './ui.tsx';

interface ImportJob {
  id: string;
  modo: string;
  estado: string;
  resumen: {
    totalFilas: number;
    creados: number;
    actualizados: number;
    errores: number;
    sinImagen: string[];
    marcasNuevas?: string[];
    credencialesGeneradas?: number;
  } | null;
  errores: { fila: number; referencia?: string; motivo: string }[] | null;
}

/**
 * Importación embebida (mismo flujo que Importaciones):
 * Excel (+ ZIP si aplica) → análisis (dry-run) → resumen → Aplicar.
 */
export function ModalImportar({
  tipo,
  titulo,
  columnas,
  conZip = false,
  plantilla,
  alCerrar,
  alAplicado,
}: {
  tipo: 'PRODUCTOS' | 'CLIENTES' | 'PEDIDOS' | 'MATERIALES';
  titulo: string;
  columnas: string;
  conZip?: boolean;
  /** Ruta de una plantilla .xlsx descargable para llenar a mano. */
  plantilla?: { ruta: string; nombre: string };
  alCerrar: () => void;
  alAplicado: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const [excel, setExcel] = useState<File | null>(null);
  /** Sólo PEDIDOS: cuánto tiempo quedan disponibles los productos comprados. */
  const [meses, setMeses] = useState('6');
  const [zip, setZip] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const job = useQuery({
    queryKey: ['import-job', jobId],
    enabled: jobId !== null,
    queryFn: () => api<ImportJob>(`/admin/importaciones/${jobId}`),
    refetchInterval: (query) => {
      const e = query.state.data?.estado;
      return e === 'PENDIENTE' || e === 'PROCESANDO' ? 1200 : false;
    },
  });

  const analizar = useMutation({
    mutationFn: async () => {
      if (!excel) throw new Error('Seleccione el archivo Excel');
      const forma = new FormData();
      forma.append('tipo', tipo);
      forma.append('excel', excel);
      if (tipo === 'PEDIDOS') forma.append('mesesDisponibilidad', meses);
      if (zip) forma.append('mediaZip', zip);
      return apiMultipart<ImportJob>('/admin/importaciones', forma);
    },
    onSuccess: (j) => {
      setError(null);
      setJobId(j.id);
    },
    onError: (e) => setError(e.message),
  });

  const aplicar = useMutation({
    mutationFn: () => api<ImportJob>(`/admin/importaciones/${jobId}/aplicar`, { metodo: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['import-job', jobId] });
    },
    onError: (e) => setError(e.message),
  });

  // Claves generadas para clientes nuevos: un solo uso, expiran a las 24 h (D10).
  async function descargarCredenciales() {
    const blob = await apiBlob(`/admin/importaciones/${jobId}/credenciales.xlsx`);
    if (!blob) {
      setError('Las credenciales ya se descargaron o expiraron.');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credenciales-${jobId}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function descargarPlantilla() {
    if (!plantilla) return;
    const blob = await apiBlob(plantilla.ruta);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = plantilla.nombre;
    a.click();
    URL.revokeObjectURL(url);
  }

  const j = job.data ?? null;
  const procesando = j !== null && (j.estado === 'PENDIENTE' || j.estado === 'PROCESANDO');
  const analisisListo = j?.estado === 'LISTO' && j.modo === 'DRY_RUN';
  const aplicado = j?.estado === 'LISTO' && j.modo === 'APPLY';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="tarjeta w-full max-w-lg space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">{titulo}</h2>

        {jobId === null ? (
          <>
            <p className="text-sm text-suave">{columnas}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="mb-1.5 text-[13px] text-suave">Excel (.xlsx)</p>
                <BotonArchivo etiqueta="Elegir Excel" accept=".xlsx" onArchivo={setExcel} nombre={excel?.name ?? null} />
                {tipo === 'PEDIDOS' ? (
                  <label className="flex items-center gap-2 text-sm">
                    <span className="text-suave">Disponible para el cliente por</span>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={meses}
                      onChange={(e) => setMeses(e.target.value)}
                      className="campo min-h-10 w-20 text-center"
                    />
                    <span className="text-suave">meses</span>
                  </label>
                ) : null}
              </div>
              {conZip ? (
                <div>
                  <p className="mb-1.5 text-[13px] text-suave">ZIP de fotos (opcional)</p>
                  <BotonArchivo etiqueta="Elegir ZIP" accept=".zip" onArchivo={setZip} nombre={zip?.name ?? null} />
                </div>
              ) : null}
            </div>
            <p className="text-xs text-suave">Primero se corre un análisis que no toca la base; después decide si aplica.</p>
            {plantilla ? (
              <Boton variante="texto" tamano="sm" type="button" onClick={() => void descargarPlantilla()}>
                Descargar plantilla de ejemplo
              </Boton>
            ) : null}
          </>
        ) : (
          <div className="space-y-2 text-sm">
            {procesando ? <p className="text-suave">{j?.modo === 'APPLY' ? 'Aplicando…' : 'Analizando…'}</p> : null}
            {j?.resumen ? (
              <div className={`space-y-1 ${aplicado ? 'font-medium text-emerald-600' : ''}`}>
                <p className="text-[15px]">
                  {aplicado ? 'Listo: ' : ''}
                  {j.resumen.totalFilas} registro(s) encontrados · {j.resumen.creados} nuevos ·{' '}
                  {j.resumen.actualizados} actualizados · {j.resumen.errores} con error
                </p>
                {j.resumen.sinImagen.length > 0 ? (
                  <p className="font-normal text-suave">
                    {j.resumen.sinImagen.length} sin foto (invisibles para los almacenes): súbalas en el
                    ZIP como codigo.png o use Buscar fotos después de aplicar.
                  </p>
                ) : null}
                {(j.resumen.marcasNuevas?.length ?? 0) > 0 ? (
                  <p className="font-normal text-suave">
                    Marcas {aplicado ? 'creadas' : 'que se crearán'}: {j.resumen.marcasNuevas!.join(', ')}
                  </p>
                ) : null}
              </div>
            ) : null}
            {j?.errores && j.errores.length > 0 ? (
              <div className="rounded-2xl bg-rose-500/10 p-3 text-xs text-rose-500">
                <ul className="max-h-32 space-y-1 overflow-y-auto">
                  {j.errores.slice(0, 10).map((e, i) => (
                    <li key={i}>
                      Fila {e.fila} {e.referencia ? `(${e.referencia})` : ''}: {e.motivo}
                    </li>
                  ))}
                </ul>
                {j.errores.length > 10 ? <p className="mt-1 font-medium">… y {j.errores.length - 10} error(es) más (detalle completo en Analítica → Historial).</p> : null}
              </div>
            ) : null}
            {j?.estado === 'ERROR' ? <Aviso>La importación falló. Revise el archivo.</Aviso> : null}
            {aplicado && (j?.resumen?.credencialesGeneradas ?? 0) > 0 ? (
              <Boton variante="contorno" type="button" onClick={() => void descargarCredenciales()}>
                Descargar credenciales.xlsx ({j!.resumen!.credencialesGeneradas})
              </Boton>
            ) : null}
          </div>
        )}

        {error ? <Aviso>{error}</Aviso> : null}

        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={aplicado ? () => void alAplicado() : alCerrar}>
            {aplicado ? 'Cerrar' : 'Cancelar'}
          </Boton>
          {jobId === null ? (
            <Boton type="button" disabled={analizar.isPending || !excel} onClick={() => analizar.mutate()}>
              {analizar.isPending ? 'Subiendo…' : 'Analizar'}
            </Boton>
          ) : analisisListo ? (
            <Boton type="button" disabled={aplicar.isPending} onClick={() => aplicar.mutate()}>
              Aplicar
            </Boton>
          ) : aplicado ? (
            <Boton type="button" onClick={() => void alAplicado()}>
              Listo
            </Boton>
          ) : null}
        </div>
      </div>
    </div>
  );
}
